import React, {Component} from 'react';
import {findDOMNode} from 'react-dom';
import {connect} from 'react-redux';

import {createAudio} from '../../utils/sounds';

import './Terminal.css';

const soundsKeystrokesURL = '/sounds/keystrokes/',
    promiseAudio = createAudio({
        enter: [
            `${soundsKeystrokesURL}enter/0.mp3`, `${soundsKeystrokesURL}enter/1.mp3`
        ],
        letter: [
            `${soundsKeystrokesURL}letter/0.mp3`,
            `${soundsKeystrokesURL}letter/1.mp3`,
            `${soundsKeystrokesURL}letter/2.mp3`,
            `${soundsKeystrokesURL}letter/3.mp3`,
            `${soundsKeystrokesURL}letter/4.mp3`,
            `${soundsKeystrokesURL}letter/5.mp3`,
            `${soundsKeystrokesURL}letter/6.mp3`,
            `${soundsKeystrokesURL}letter/7.mp3`,
            `${soundsKeystrokesURL}letter/8.mp3`,
            `${soundsKeystrokesURL}letter/9.mp3`,
            `${soundsKeystrokesURL}letter/10.mp3`,
            `${soundsKeystrokesURL}letter/11.mp3`,
            `${soundsKeystrokesURL}letter/12.mp3`,
            `${soundsKeystrokesURL}letter/13.mp3`,
            `${soundsKeystrokesURL}letter/14.mp3`,
            `${soundsKeystrokesURL}letter/15.mp3`,
            `${soundsKeystrokesURL}letter/16.mp3`,
            `${soundsKeystrokesURL}letter/17.mp3`
        ],
        space: [`${soundsKeystrokesURL}space/0.mp3`, `${soundsKeystrokesURL}space/1.mp3`, `${soundsKeystrokesURL}space/2.mp3`]
    }),
    getDuration = (max) => Math.random() * 3 * max / 4 + max / 4;

class DesktopTerminalLine extends Component {

    componentDidMount() {
        const {duration, onComplete} = this.props,
            d = getDuration(duration || 900);
        this.timeout = setTimeout(onComplete, d);
    }

    componentWillUnmount() {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }

    render() {
        return <div className="DesktopTerminalLine">{this.props.children}</div>;
    }
}

class DesktopTerminalCommand extends Component {
    constructor(props) {
        super(props);
        const {children} = props;
        this.state = {
            characters: children
                ? children.split('')
                : [],
            output: '',
            ready: false
        };
        this.audio = null;
    }

    getSoundName = (character) => {
        switch (character) {
            case undefined:
                return 'enter';
            case ' ':
                return 'space';
            default:
                return 'letter';
        }
    }

    nextStep = () => {
        const {duration, onComplete, volume} = this.props, {characters, output} = this.state,
            character = characters.shift(),
            d = getDuration(duration || 300),
            name = this.getSoundName(character),
            sound = this
                .audio
                .getSound(name);
        sound.volume = volume;
        sound.play();
        if (character === undefined) {
            this.timeout = setTimeout(onComplete, d);
        } else {
            this.timeout = setTimeout(this.nextStep, d);
            this.setState({
                characters,
                output: output + character
            });
        }
    }

    componentDidMount() {
        promiseAudio.then(audio => {
            this.audio = audio;
            this.setState({ready: true});
        });
    }

    shouldComponentUpdate(props, {ready, output}) {
        return (ready && output !== this.state.output) || ready !== this.state.ready;
    }

    componentDidUpdate() {
        const shell = findDOMNode(this.refs.line).parentNode;
        shell.scrollTop = shell.scrollHeight;
    }

    componentWillUnmount() {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }

    render() {
        const {dir} = this.props, {ready, output} = this.state;
        return ready
            ? <DesktopTerminalLine ref="line" onComplete={this.nextStep}>
                    [<span className="red">{dir}</span>]<br/>| &gt; {output}
                </DesktopTerminalLine>
            : null;
    }
}

class DesktopTerminalProgress extends Component {

    state = {
        step: 0
    }

    nextStep = () => {
        const {children, duration, onComplete} = this.props, {step} = this.state,
            d = getDuration(duration || 600);
        if (step === children.length) {
            this.timeout = setTimeout(onComplete, d);
        } else {
            this.timeout = setTimeout(this.nextStep, d);
            this.setState({
                step: step + 1
            });
        }
    }

    shouldComponentUpdate(props, {step}) {
        return step !== this.state.step;
    }

    componentDidUpdate() {
        const shell = findDOMNode(this.refs.line).parentNode;
        shell.scrollTop = shell.scrollHeight;
    }

    componentWillUnmount() {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }

    render() {
        const {children, size} = this.props, {step} = this.state,
            progress = step / children.length,
            progressSize = size || 18,
            progressValue = Math.round(progress * progressSize),
            bar = '█'.repeat(progressValue) + '░'.repeat(progressSize - progressValue),
            loader = [
                '⠇',
                '⠋',
                '⠙',
                '⠸',
                '⠴',
                '⠦'
            ][step % 6];
        return <DesktopTerminalLine ref="line" onComplete={this.nextStep}>{
                step === children.length
                    ? null
                    : <span>⸨{bar}⸩&nbsp;{loader}&nbsp;{children[step]}</span>
            }</DesktopTerminalLine>;
    }
}

class DesktopTerminalShell extends Component {

    constructor(props) {
        super(props);
        const {children} = props,
            indexedChild = (child, key) => React.cloneElement(child, {key, onComplete: this.nextStep});
        this.state = {
            children: children.map(indexedChild),
            step: 1
        };
    }

    nextStep = () => {
        const {duration, onComplete} = this.props, {children, step} = this.state;
        if (step === children.length) {
            const d = getDuration(duration || 1200);
            this.timeout = onComplete
                ? setTimeout(onComplete, d)
                : null;
        } else {
            this.setState({
                step: step + 1
            });
        }
    }

    componentDidUpdate() {
        this.refs.shell.scrollTop = this.refs.shell.scrollHeight;
    }

    shouldComponentUpdate(props, {step}) {
        return step !== this.state.step;
    }

    componentWillUnmount() {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }

    render() {
        const {children, step} = this.state;
        return <div className="DesktopTerminalShell" ref="shell">{children.slice(0, step)}</div>;
    }
}

export default({
    Command: connect(({settings: {
            volume
        }}) => ({volume}))(DesktopTerminalCommand),
    Line: DesktopTerminalLine,
    Progress: DesktopTerminalProgress,
    Shell: DesktopTerminalShell
});


// WEBPACK FOOTER //
// ./src/components/desktop/Terminal.js